import React, {useCallback, useEffect, useState} from "react";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Container,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import {useUserAuth} from "../../context/UserAuthContext";
import {searchProjects} from "../../services/associationService";
import {ProjectCardMedia} from "../common/ProjectImage";
import PortalTopbar from "./PortalTopbar";

const PRIMARY = "#3E4F88";

/**
 * Home do portal de associações:
 * lista de projetos da associação (aprovados), pesquisa e "adicionar projeto".
 * Espelha a HomePage da app (mesmo filtro: Association == nome e Enabled).
 */
const PortalHome = () => {

    const {profile} = useUserAuth();
    const navigate = useNavigate();

    const [projects, setProjects] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const associationName = profile && profile.Name ? profile.Name : "";

    const load = useCallback(async (term) => {
        if (!associationName) {
            setProjects([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const result = await searchProjects(associationName, term);
            setProjects(result);
            setError("");
        } catch (err) {
            console.error("Erro ao carregar projetos:", err);
            setError("Não foi possível carregar os projetos. Tenta atualizar a página.");
        } finally {
            setLoading(false);
        }
    }, [associationName]);

    useEffect(() => {
        load(search);
    }, [load, search]);

    return (
        <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
            <PortalTopbar />
            <Container maxWidth="lg" sx={{py: 5}}>
                <Stack spacing={3}>
                    <Stack direction={{xs: "column", sm: "row"}} alignItems={{sm: "center"}} spacing={2} justifyContent="space-between">
                        <Box>
                            <Typography variant="h4" sx={{fontWeight: 700, color: PRIMARY}}>
                                {profile?.Name ? `Olá, ${profile.Name}` : "Olá"}
                            </Typography>
                            <Typography color="text.secondary">
                                Os vossos projetos na plataforma E4IU.
                            </Typography>
                        </Box>
                        <Button
                            component={RouterLink}
                            to="/app/associacao/projetos/novo"
                            variant="contained"
                            startIcon={<AddIcon/>}
                            sx={{bgcolor: PRIMARY, fontWeight: 600}}
                        >
                            Adicionar projeto
                        </Button>
                    </Stack>

                    <TextField
                        size="small"
                        placeholder="Pesquisar por título, localização ou curso…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action"/>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {error && <Alert severity="error">{error}</Alert>}

                    {loading ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                            <CircularProgress/>
                        </Box>
                    ) : projects.length === 0 ? (
                        <Alert severity="info" sx={{alignItems: "flex-start", textAlign: "left"}}>
                            {search
                                ? <>Nenhum projeto encontrado para “{search}”.</>
                                : <>Ainda não há projetos aprovados para a vossa associação. Cria o primeiro com o botão “Adicionar projeto”.</>}
                        </Alert>
                    ) : (
                        <Box
                            sx={{
                                display: "grid",
                                gap: 3,
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(2, 1fr)",
                                    md: "repeat(3, 1fr)",
                                },
                            }}
                        >
                            {projects.map((p) => (
                                <Card key={p.key} sx={{height: "100%"}}>
                                    <CardActionArea onClick={() => navigate(`/app/associacao/projetos/${p.Id}`)} sx={{height: "100%"}}>
                                        <ProjectCardMedia src={p.Logo} alt={p.Title}/>
                                        <CardContent sx={{pb: 2}}>
                                            <Typography variant="h6" sx={{fontWeight: 700, color: PRIMARY, mb: 1}}>
                                                {p.Title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                                🎓 {p.College}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                                📍 {p.Location}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {p.Time || "—"} · {p.Type || "—"}
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Box>
                    )}
                </Stack>
            </Container>
        </Box>
    );
};

export default PortalHome;